import type { FillStyle, LineStyle, TextRun } from '@/api/types/stowed-element'
import { DEFAULT_FONT_FAMILY_ID, DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import type { ParagraphBlockFontLookup } from '@/lib/text/types/paragraph-block-font-lookup'
import type { TextRunStyle } from '@/lib/text/types/text-run-style'

/** Marks a `<span>` as a styled text run in clipboard HTML. */
export const RUN_SPAN_ATTR = 'data-text-run'

const RUN_FONT_ID_ATTR = 'data-run-fontid'
const RUN_FONT_FAMILY_ID_ATTR = 'data-run-fontfamilyid'

/** Exported so the clipboard parser can tell larry's own spans from foreign HTML — a larry span's
 *  CSS `font-size` is in document points, not 96dpi px. See `parse-external-clipboard-html.ts`. */
export const RUN_POINT_SIZE_ATTR = 'data-run-pointsize'

const RUN_UNDERLINE_ATTR = 'data-run-underline'
const RUN_STRIKETHROUGH_ATTR = 'data-run-strikethrough'
/** WMPCM-10141 — canvas-model-only, see `TextRun.verticalAlign`'s own doc comment. */
const RUN_VERTICAL_ALIGN_ATTR = 'data-run-vertical-align'
const RUN_KERNING_ATTR = 'data-run-kerning'
const RUN_TRACKING_ATTR = 'data-run-tracking'
const RUN_LEADING_ATTR = 'data-run-leading'
// yb360 color styles, JSON-encoded (small {colorId,tint,...} objects) — carried through the
// clipboard so loaded colors survive a copy/paste even though this editor doesn't render them.
const RUN_LINE_STYLE_ATTR = 'data-run-line-style'
const RUN_FILL_STYLE_ATTR = 'data-run-fill-style'

function setNumberAttr(el: HTMLElement, name: string, value: number | undefined): void {
  if (value == null || !Number.isFinite(value)) {
    el.removeAttribute(name)
  } else {
    el.setAttribute(name, String(value))
  }
}

/** Visual typography CSS only (font-family/size/weight/style/decoration/letter-spacing), so the
 *  copied HTML renders in the run's own font when pasted into another editor. */
function applyTypographyStyle(
  el: HTMLElement,
  run: TextRun,
  fonts?: ParagraphBlockFontLookup
): void {
  const famId = run.fontFamilyId ?? DEFAULT_FONT_FAMILY_ID
  const cssFamily =
    famId && fonts?.getFamilyCssName(famId)
      ? `"${fonts.getFamilyCssName(famId)}", sans-serif`
      : 'sans-serif'
  const variant = run.fontId != null ? fonts?.getVariantByFontId(run.fontId) : undefined
  const track = run.tracking ?? 0
  const kern = run.kerning ?? 0
  // tracking and kerning are both stored as 1/1000 em (print industry convention); convert to em for CSS
  const letterSpacingEm = (track + kern) / 1000

  el.style.fontFamily = cssFamily
  // `px`: a point size is a length in document units and 1pt = 1 CSS px in the canvas (see
  // `canvas-font-metrics.ts`).
  el.style.fontSize = `${run.pointSize ?? DEFAULT_POINT_SIZE}px`
  el.style.fontWeight = variant?.bold ? '700' : '400'
  el.style.fontStyle = variant?.italic ? 'italic' : 'normal'
  // Unkerned, matching the wire measurer (WMPCM-8161).
  el.style.fontKerning = 'none'
  el.style.fontVariantLigatures = 'none'
  // Leading travels in `RUN_LEADING_ATTR`, never as CSS line-height.
  el.style.lineHeight = 'normal'
  const decs = [run.underline && 'underline', run.strikethrough && 'line-through'].filter(Boolean)

  el.style.textDecoration = decs.join(' ')
  el.style.letterSpacing = letterSpacingEm !== 0 ? `${letterSpacingEm}em` : ''
  el.style.whiteSpace = 'pre-wrap'
}

/** Encode a run's style onto a span as `data-*` attributes plus inline CSS. */
export function applyRunSpanStyle(
  span: HTMLElement,
  run: TextRun,
  fonts?: ParagraphBlockFontLookup
): void {
  span.setAttribute(RUN_SPAN_ATTR, '1')
  setNumberAttr(span, RUN_FONT_ID_ATTR, run.fontId ?? undefined)
  setNumberAttr(span, RUN_FONT_FAMILY_ID_ATTR, run.fontFamilyId ?? undefined)
  setNumberAttr(span, RUN_POINT_SIZE_ATTR, run.pointSize ?? undefined)
  setNumberAttr(span, RUN_KERNING_ATTR, run.kerning ?? undefined)
  setNumberAttr(span, RUN_TRACKING_ATTR, run.tracking ?? undefined)

  if (run.underline) span.setAttribute(RUN_UNDERLINE_ATTR, '1')
  else span.removeAttribute(RUN_UNDERLINE_ATTR)

  if (run.strikethrough) span.setAttribute(RUN_STRIKETHROUGH_ATTR, '1')
  else span.removeAttribute(RUN_STRIKETHROUGH_ATTR)

  if (run.leading != null && run.leading !== '') span.setAttribute(RUN_LEADING_ATTR, run.leading)
  else span.removeAttribute(RUN_LEADING_ATTR)

  if (run.lineStyle != null) span.setAttribute(RUN_LINE_STYLE_ATTR, JSON.stringify(run.lineStyle))
  else span.removeAttribute(RUN_LINE_STYLE_ATTR)

  if (run.fillStyle != null) span.setAttribute(RUN_FILL_STYLE_ATTR, JSON.stringify(run.fillStyle))
  else span.removeAttribute(RUN_FILL_STYLE_ATTR)

  if (run.verticalAlign != null) span.setAttribute(RUN_VERTICAL_ALIGN_ATTR, run.verticalAlign)
  else span.removeAttribute(RUN_VERTICAL_ALIGN_ATTR)

  applyTypographyStyle(span, run, fonts)
}

/** Create a styled run span with the given text content. */
export function createRunSpan(run: TextRun, fonts?: ParagraphBlockFontLookup): HTMLSpanElement {
  const span = document.createElement('span')

  applyRunSpanStyle(span, run, fonts)
  span.textContent = run.textContent ?? ''

  return span
}

/** Decode a run span's `data-*` attributes back into a run style. */
export function readRunStyleFromSpan(span: Element): TextRunStyle {
  const num = (name: string): number | undefined => {
    const raw = span.getAttribute(name)

    if (raw == null) return undefined
    const n = Number(raw)

    return Number.isFinite(n) ? n : undefined
  }
  const style: TextRunStyle = {}
  const fontId = num(RUN_FONT_ID_ATTR)

  if (fontId !== undefined) style.fontId = fontId
  const fontFamilyId = num(RUN_FONT_FAMILY_ID_ATTR)

  if (fontFamilyId !== undefined) style.fontFamilyId = fontFamilyId
  const pointSize = num(RUN_POINT_SIZE_ATTR)

  if (pointSize !== undefined) style.pointSize = pointSize
  const kerning = num(RUN_KERNING_ATTR)

  if (kerning !== undefined) style.kerning = kerning
  const tracking = num(RUN_TRACKING_ATTR)

  if (tracking !== undefined) style.tracking = tracking
  style.underline = span.getAttribute(RUN_UNDERLINE_ATTR) === '1'
  style.strikethrough = span.getAttribute(RUN_STRIKETHROUGH_ATTR) === '1'
  const leading = span.getAttribute(RUN_LEADING_ATTR)

  if (leading != null && leading !== '') style.leading = leading
  const lineStyleRaw = span.getAttribute(RUN_LINE_STYLE_ATTR)

  if (lineStyleRaw) {
    try {
      style.lineStyle = JSON.parse(lineStyleRaw) as LineStyle
    } catch {
      // Malformed attribute — fall back to no explicit style (≡ default).
    }
  }

  const fillStyleRaw = span.getAttribute(RUN_FILL_STYLE_ATTR)

  if (fillStyleRaw) {
    try {
      style.fillStyle = JSON.parse(fillStyleRaw) as FillStyle
    } catch {
      // Malformed attribute — fall back to no explicit style (≡ default).
    }
  }

  const verticalAlignRaw = span.getAttribute(RUN_VERTICAL_ALIGN_ATTR)

  if (verticalAlignRaw === 'super' || verticalAlignRaw === 'sub') {
    style.verticalAlign = verticalAlignRaw
  }

  return style
}
